// useNotiClaimOrchestrator.ts

import { useCallback, useEffect, useRef } from "react";
import { WorldAuthClient } from "@bridge-applications/world-auth";
import {
    ApiNotiClaimCompleteReqSchema,
    ApiNotiClaimCompleteResSchema,
    ApiNotiClaimPendingReqSchema,
    ApiNotiClaimPendingResSchema,
} from "@bridge-applications/eh-types";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { API_BASE } from "../../state/config";
import { claim } from "../../claim";
import EhUniversalVoucher from "../../abi/EhUniversalVoucher.json";

export const NOTI_CLAIM_POLL_KEY = "__earnHub__noti_claim_poll_v1";

/** Query param on the notification deep link: `/?noti=<id>&claim=<notiClaimId>`. */
export const NOTI_CLAIM_QUERY_PARAM = "claim";

/** How long we keep trying after a deep-link open (covers the login delay). */
const POLL_WINDOW_MS = 15 * 60 * 1000;

const NOTI_CLAIM_ID_RE = /^[A-Za-z0-9_-]{1,64}$/;

type NotiClaimPollIntent = {
    type: "NOTI_CLAIM";
    notiClaimId: string;
    startedAt: number;
    pollUntil: number;
};

function readPollIntent(): NotiClaimPollIntent | null {
    if (typeof window === "undefined") return null;

    try {
        const raw = localStorage.getItem(NOTI_CLAIM_POLL_KEY);
        if (!raw) return null;

        const parsed = JSON.parse(raw) as Partial<NotiClaimPollIntent>;
        if (
            parsed.type !== "NOTI_CLAIM" ||
            typeof parsed.notiClaimId !== "string" ||
            typeof parsed.startedAt !== "number" ||
            typeof parsed.pollUntil !== "number"
        ) {
            localStorage.removeItem(NOTI_CLAIM_POLL_KEY);
            return null;
        }

        if (parsed.pollUntil <= Date.now()) {
            localStorage.removeItem(NOTI_CLAIM_POLL_KEY);
            return null;
        }

        return parsed as NotiClaimPollIntent;
    } catch {
        try {
            localStorage.removeItem(NOTI_CLAIM_POLL_KEY);
        } catch {
            /* noop */
        }
        return null;
    }
}

function clearPollIntent(): void {
    if (typeof window === "undefined") return;
    try {
        localStorage.removeItem(NOTI_CLAIM_POLL_KEY);
    } catch {
        /* noop */
    }
}

/**
 * If the app was opened from a noti-claim deep link (`?claim=<id>`), persist a
 * poll intent and strip the param so a refresh/back-nav doesn't re-seed it. The
 * grant is pre-seeded server-side, so the orchestrator resolves on the first
 * authenticated poll.
 */
function seedIntentFromUrl(): void {
    if (typeof window === "undefined") return;

    try {
        const url = new URL(window.location.href);
        const notiClaimId = url.searchParams.get(NOTI_CLAIM_QUERY_PARAM);
        if (!notiClaimId || !NOTI_CLAIM_ID_RE.test(notiClaimId)) return;

        const existing = readPollIntent();
        if (!existing || existing.notiClaimId !== notiClaimId) {
            const now = Date.now();
            const intent: NotiClaimPollIntent = {
                type: "NOTI_CLAIM",
                notiClaimId,
                startedAt: now,
                pollUntil: now + POLL_WINDOW_MS,
            };
            localStorage.setItem(NOTI_CLAIM_POLL_KEY, JSON.stringify(intent));
        }

        // Remove only the claim param; leave `noti` (telemetry) intact.
        url.searchParams.delete(NOTI_CLAIM_QUERY_PARAM);
        window.history.replaceState(
            window.history.state,
            "",
            `${url.pathname}${url.search}${url.hash}`,
        );
    } catch {
        /* noop */
    }
}

function isVisible(): boolean {
    return (
        typeof document === "undefined" ||
        document.visibilityState === "visible"
    );
}

function getNextPollDelayMs(intent: NotiClaimPollIntent): number {
    const elapsed = Date.now() - intent.startedAt;
    if (elapsed < 2 * 60 * 1000) return 2 * 1000;
    if (elapsed < 5 * 60 * 1000) return 10 * 1000;
    return 30 * 1000;
}

function getTerminalErrorKey(message: string): string | null {
    if (message === "CLAIM_EXPIRED") return "NotiClaim.expired";
    if (message === "WALLET_MISMATCH") return "NotiClaim.walletMismatch";
    if (message === "USER_BANNED") return "NotiClaim.error";
    if (message === "VOUCHER_FAILED") return "NotiClaim.error";
    if (message === "CLAIM_NOT_FOUND") return "NotiClaim.error";
    if (message === "MINIKIT_CLAIM_FAILED") return "NotiClaim.error";
    if (message === "COMPLETE_FAILED") return "NotiClaim.error";
    return null;
}

type UseNotiClaimOrchestratorOptions = {
    appSessionLoading: boolean | undefined;
};

export function useNotiClaimOrchestrator({
    appSessionLoading,
}: UseNotiClaimOrchestratorOptions): void {
    const { t } = useTranslation();
    const inFlightRef = useRef(false);
    const timerRef = useRef<number | null>(null);
    const processPendingClaimRef = useRef<() => void>(() => undefined);

    const clearTimer = useCallback(() => {
        if (timerRef.current !== null) {
            window.clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    }, []);

    const scheduleNextPoll = useCallback(
        (intent: NotiClaimPollIntent) => {
            clearTimer();
            if (!isVisible()) return;

            const remaining = intent.pollUntil - Date.now();
            if (remaining <= 0) {
                clearPollIntent();
                return;
            }

            const delay = Math.min(getNextPollDelayMs(intent), remaining);
            timerRef.current = window.setTimeout(() => {
                processPendingClaimRef.current();
            }, delay);
        },
        [clearTimer],
    );

    const processPendingClaim = useCallback(() => {
        const intent = readPollIntent();
        if (!intent) {
            clearTimer();
            return;
        }

        if (!isVisible()) return;
        if (appSessionLoading !== false) {
            scheduleNextPoll(intent);
            return;
        }
        if (inFlightRef.current) return;

        inFlightRef.current = true;

        const run = async () => {
            let toastId: string | undefined;

            try {
                const accessToken =
                    await WorldAuthClient.getAccessTokenSilently();
                if (!accessToken) {
                    throw new Error("UNAUTHENTICATED");
                }

                const pendingRes = await fetch(
                    `${API_BASE}/api/v1/noti-claim/pending`,
                    {
                        method: "POST",
                        headers: {
                            "content-type": "application/json",
                            authorization: `Bearer ${accessToken.accessToken}`,
                        },
                        body: JSON.stringify(
                            ApiNotiClaimPendingReqSchema.parse({
                                noti_claim_id: intent.notiClaimId,
                            }),
                        ),
                    },
                );

                const pendingJson = await pendingRes.json().catch(() => null);
                const pending =
                    ApiNotiClaimPendingResSchema.safeParse(pendingJson);

                if (!pending.success) {
                    throw new Error("PENDING_FAILED");
                }
                if (pending.data.status === "ERROR") {
                    throw new Error(pending.data.code ?? "PENDING_FAILED");
                }
                if (!pendingRes.ok) {
                    throw new Error("PENDING_FAILED");
                }

                // No grant for this user → nothing to claim; stop silently.
                if (pending.data.status === "NONE") {
                    clearPollIntent();
                    return;
                }

                if (pending.data.status === "EXPIRED") {
                    clearPollIntent();
                    toast.error(
                        t("NotiClaim.expired", {
                            defaultValue: "This reward link has expired.",
                        }),
                    );
                    return;
                }

                if (pending.data.status === "FULFILLED") {
                    clearPollIntent();
                    toast.success(
                        t("NotiClaim.alreadyClaimed", {
                            defaultValue: "Reward already claimed.",
                        }),
                    );
                    return;
                }

                toastId = toast.loading(
                    t("NotiClaim.resolving", {
                        defaultValue: "Preparing your reward…",
                    }),
                );

                const { voucher } = pending.data;
                const verifyingContract = voucher.domain.verifyingContract;

                toast.loading(
                    t("NotiClaim.claiming", {
                        defaultValue: "Claiming reward…",
                    }),
                    { id: toastId },
                );

                const txRes = await claim([
                    {
                        address: verifyingContract,
                        abi: EhUniversalVoucher.abi,
                        functionName: "claim",
                        args: [voucher.struct, voucher.signature],
                    },
                ]);

                if ((txRes as { status?: string })?.status === "error") {
                    throw new Error("MINIKIT_CLAIM_FAILED");
                }

                const transactionId = (txRes as { transaction_id?: string })
                    ?.transaction_id;

                const completeRes = await fetch(
                    `${API_BASE}/api/v1/noti-claim/complete`,
                    {
                        method: "POST",
                        headers: {
                            "content-type": "application/json",
                            authorization: `Bearer ${accessToken.accessToken}`,
                        },
                        body: JSON.stringify(
                            ApiNotiClaimCompleteReqSchema.parse({
                                noti_claim_id: intent.notiClaimId,
                                transaction_id: transactionId,
                            }),
                        ),
                    },
                );

                const completeJson = await completeRes.json().catch(() => null);
                const completed =
                    ApiNotiClaimCompleteResSchema.safeParse(completeJson);

                if (!completed.success) {
                    throw new Error("COMPLETE_FAILED");
                }
                if (completed.data.status === "ERROR") {
                    throw new Error(completed.data.code ?? "COMPLETE_FAILED");
                }
                if (!completeRes.ok) {
                    throw new Error("COMPLETE_FAILED");
                }

                clearPollIntent();

                const alreadyFulfilled =
                    completed.data.state.status === "ALREADY_FULFILLED";

                toast.success(
                    alreadyFulfilled
                        ? t("NotiClaim.alreadyClaimed", {
                              defaultValue: "Reward already claimed.",
                          })
                        : t("NotiClaim.success", {
                              defaultValue: "Reward claimed!",
                          }),
                    { id: toastId },
                );
            } catch (err: unknown) {
                const message =
                    err instanceof Error ? err.message : String(err);
                const terminalErrorKey = getTerminalErrorKey(message);

                if (terminalErrorKey) {
                    clearPollIntent();
                } else {
                    scheduleNextPoll(intent);
                }

                if (toastId || terminalErrorKey) {
                    toast.error(
                        t(
                            message === "UNAUTHENTICATED"
                                ? "NotiClaim.signInRequired"
                                : (terminalErrorKey ?? "NotiClaim.error"),
                            {
                                defaultValue:
                                    message === "UNAUTHENTICATED"
                                        ? "Sign in to claim your reward."
                                        : "Could not claim reward. Please try again.",
                            },
                        ),
                        toastId ? { id: toastId } : undefined,
                    );
                }
            } finally {
                inFlightRef.current = false;
            }
        };

        void run();
    }, [appSessionLoading, clearTimer, scheduleNextPoll, t]);

    processPendingClaimRef.current = processPendingClaim;

    useEffect(() => {
        seedIntentFromUrl();
    }, []);

    useEffect(() => {
        processPendingClaim();
    }, [processPendingClaim]);

    useEffect(() => {
        const onResume = () => {
            processPendingClaim();
        };
        const onVisibilityChange = () => {
            if (document.visibilityState === "visible") {
                processPendingClaim();
            }
        };

        window.addEventListener("pageshow", onResume);
        window.addEventListener("focus", onResume);
        document.addEventListener("visibilitychange", onVisibilityChange);

        return () => {
            window.removeEventListener("pageshow", onResume);
            window.removeEventListener("focus", onResume);
            document.removeEventListener(
                "visibilitychange",
                onVisibilityChange,
            );
        };
    }, [processPendingClaim]);

    useEffect(() => {
        return () => {
            clearTimer();
        };
    }, [clearTimer]);
}
